import { memo, useCallback } from "react";

import type { HotelEdit_DesignPageQuery } from "@/lib/gql/__generated__/graphql";
import { LangCode } from "@/lib/gql/__generated__/graphql";

import { useTransitionBlock } from "@/hooks/use-transition-block";

import { FeatureIslandHeader } from "@/components/features/_layouts";

import { Button } from "@/components/ui/button/button";

import { useHandleSettingTag } from "../../../../_shared/hooks";
import { MagazineEditor } from "../../../../_shared/ui/component/editor/magazine-editor";
import { DesignMagazineHeaderRightChildren } from "../../../../_shared/ui/component/header/right-children";
import { getMagazineEditPageUrl } from "../../../../_shared/utils/get-magazine-edit-page-url";
import { DesignMagazineSettingTagContainer } from "../../../../setting-tag";
import { useInitialized } from "../../model/usecase/use-initialized";

type Props = {
	hotelID: string;
	data: HotelEdit_DesignPageQuery;
};

export const DesignMagazineHotelArticleDefaultEditPresenter = memo(({ hotelID, data: _data }: Props) => {
	const { articleHTML, onSetArticleHTML, tag, onSetTag, isValid, isDirty, data, onUpdate } = useInitialized({
		hotelID,
		data: _data,
	});
	const { name } = data.hotel;

	useTransitionBlock(isDirty);

	const { canShowSettingTag, onChangeCanShowSettingTag } = useHandleSettingTag();
	/**
	 * タグ設定画面の表示
	 */
	const onShowSettingTag = useCallback(() => onChangeCanShowSettingTag(true), [onChangeCanShowSettingTag]);

	return (
		<div>
			<FeatureIslandHeader
				className="top-0"
				title="施設記事の編集"
				activeLangCode={LangCode.Default}
				navigator={{
					default: getMagazineEditPageUrl({
						hotelID,
						magazineResourceID: undefined,
						magazineResourceType: "AboutHotel",
					}),
					i18n: (lang) =>
						getMagazineEditPageUrl({
							hotelID,
							magazineResourceID: undefined,
							magazineResourceType: "AboutHotel",
							langCode: lang,
						}),
				}}
				rightChildren={
					<DesignMagazineHeaderRightChildren
						isValid={isValid}
						onUpdate={onUpdate}
						settingTagButtonChildren={
							<Button
								size="xl"
								className="flex-1 whitespace-nowrap px-6 laptop:flex-none laptop:px-14"
								type="button"
								variant="outline"
								onClick={onShowSettingTag}
							>
								タグをつける
							</Button>
						}
					/>
				}
			/>

			{Boolean(articleHTML) && (
				<MagazineEditor
					resourceName={name ?? ""}
					tiptapEditorProps={{
						content: articleHTML,
						hotelID,
						handleEditorContentChange: onSetArticleHTML,
					}}
				/>
			)}

			{canShowSettingTag && (
				<DesignMagazineSettingTagContainer
					open={canShowSettingTag}
					tagList={tag.tagList}
					regionTag={tag.regionTag}
					prefectureTag={tag.prefectureTag}
					cityTag={tag.cityTag}
					onFinishCallback={onSetTag}
					onOpenChange={onChangeCanShowSettingTag}
				/>
			)}
		</div>
	);
});

DesignMagazineHotelArticleDefaultEditPresenter.displayName = "DesignMagazineHotelArticleDefaultEditPresenter";
